import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Row, Col, Select, TimePicker, Switch } from 'antd';
import useApi from '../../hooks/useApi';
import useUserType from '../../hooks/useUserType';
import moment from 'moment';

const ModalEdit = ({ visible, id, onCancel, onConfirm, initialData }) => {
  const [form] = Form.useForm();
  const [departamentos, setDepartamentos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [tipoUsuario, setTipoUsuario] = useState(initialData?.tipo_usuario || 'usuario');
  const userType = useUserType();

  const { callApi: editUsuario } = useApi(`https://vision.alfama.com/api/update/usuario/${id}`, {
    method: 'PUT',
    includeToken: true,
    successMessage: 'Usuário atualizado com sucesso!',
    errorMessage: 'Erro ao atualizar usuário'
  });

  const { callApi: viewDepartamentos } = useApi('https://vision.alfama.com/api/view/departamentos', { 
    includeToken: true,
  });

  const { callApi: viewGrupos } = useApi('https://vision.alfama.com/api/view/grupos_permissao/ativos', { 
    includeToken: true,
  });

  useEffect(() => {
    const fetchDepartamentos = async () => {
      try {
        const response = await viewDepartamentos();
        if (response && Array.isArray(response)) {
          const formattedDepartamentos = response.map(departamento => ({
            value: departamento.departamento_id,
            label: departamento.nome_departamento
          }));
          setDepartamentos(formattedDepartamentos);
        }
      } catch (error) {
        console.error('Erro ao buscar departamentos:', error);
      }
    };
    fetchDepartamentos();
  }, [viewDepartamentos]);

  useEffect(() => {
    const fetchGrupos = async () => {
      try {
        const response = await viewGrupos();
        if (response && Array.isArray(response)) {
          const formattedGrupos = response.map(grupo => ({
            value: grupo.grupo_id,
            label: `${grupo.nome_grupo}`
          }));
          setGrupos(formattedGrupos);
        }
      } catch (error) {
        console.error('Erro ao buscar grupos:', error);
      }
    };
    fetchGrupos();
  }, [viewGrupos]);

  useEffect(() => {
    if (initialData) {
      setTipoUsuario(initialData.tipo_usuario);
  
      // Transformar permissoes em array, independentemente do formato
      let permissoesArray = [];
      if (initialData.permissoes) {
        if (Array.isArray(initialData.permissoes)) {
          permissoesArray = initialData.permissoes; // Já é um array
        } else if (typeof initialData.permissoes === 'string') {
          try {
            permissoesArray = JSON.parse(initialData.permissoes); // Tentar parsear string JSON
            if (!Array.isArray(permissoesArray)) {
              permissoesArray = [permissoesArray]; // Garantir que seja um array
            }
          } catch {
            permissoesArray = initialData.permissoes
              .split(',')
              .map(id => parseInt(id.trim()))
              .filter(Number.isInteger); // Converter strings separadas por vírgula em números
          }
        } else {
          permissoesArray = [initialData.permissoes]; // Tratar como único valor
        }
      }
  
      // Filtrar as permissões para manter apenas as que existem nos grupos disponíveis
      if (grupos.length > 0) {
        const gruposIds = grupos.map(grupo => grupo.value);
        permissoesArray = permissoesArray.filter(permissao => gruposIds.includes(permissao));
      }
  
      form.setFieldsValue({
        nome: initialData.nome,
        email: initialData.email,
        departamento: initialData.departamento_id,
        tipo_usuario: initialData.tipo_usuario,
        nome_usuario: initialData.nome_usuario,
        permissoes: permissoesArray,
        situacao: initialData.status === 'ativo',
        horario_entrada: initialData.horario_entrada
          ? moment(initialData.horario_entrada, 'HH:mm:ss')
          : moment('08:00:00', 'HH:mm:ss'),
        horario_saida: initialData.horario_saida
          ? moment(initialData.horario_saida, 'HH:mm:ss')
          : moment('18:00:00', 'HH:mm:ss')
      });
    }
  }, [initialData, form, grupos]);
  

  const handleTipoUsuarioChange = (value) => {
    setTipoUsuario(value);
    if (value === 'administrador') {
      form.setFieldValue('permissoes', []);
    }
  };

  const handleConfirm = async () => {
    try {
      const values = await form.validateFields();
      const dataToSend = {
        ...values,
        horario_entrada: values.horario_entrada?.format('HH:mm:ss'),
        horario_saida: values.horario_saida?.format('HH:mm:ss'),
        departamento_id: values.departamento,
        permissoes: values.permissoes || [],
        situacao: values.situacao ? 1 : 0
      };

      if (!values.senha) {
        delete dataToSend.senha;
      }

      const response = await editUsuario(dataToSend);
      if (response) {
        onConfirm(true);
      }
    } catch (error) {
      console.error('Erro ao editar usuário:', error);
      if (!error.errorFields) {
        onConfirm(false);
      }
    }
  };

  return (
    <Modal
      title={`Editar Usuário - ID: ${id}`}
      open={visible}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      onOk={handleConfirm}
      okText="Salvar"
      cancelText="Cancelar"
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          horario_entrada: moment('08:00:00', 'HH:mm:ss'),
          horario_saida: moment('18:00:00', 'HH:mm:ss')
        }}
      >
        {/* Formulário dividido por seções */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="nome"
              label="Nome Completo"
              rules={[{ required: true, message: 'Por favor, insira o nome completo' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Por favor, insira o email' },
                { type: 'email', message: 'Por favor, insira um email válido' }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        {/* Departamentos e tipo de usuário */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="departamento"
              label="Departamento"
              rules={[{ required: true, message: 'Por favor, selecione um departamento' }]}
            >
              <Select
                placeholder="Selecione um departamento"
                disabled={userType !== 'administrador'}
                options={departamentos}
                loading={departamentos.length === 0}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="tipo_usuario"
              label="Tipo de Usuário"
              rules={[{ required: true }]}
            >
              <Select
                onChange={handleTipoUsuarioChange}
                disabled={userType !== 'administrador'}
                options={[
                  { value: 'administrador', label: 'Administrador' },
                  { value: 'gerenciador', label: 'Gerenciador' },
                  { value: 'usuario', label: 'Usuário' }
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Grupos de permissão */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="permissoes"
              label="Grupos de Permissão"
              rules={[
                {
                  required: tipoUsuario !== 'administrador',
                  message: 'Por favor, selecione ao menos uma permissão'
                }
              ]}
            >
              <Select
                mode="multiple"
                placeholder={
                  tipoUsuario === 'administrador'
                    ? 'Administrador tem acesso a todas as permissões'
                    : 'Selecione as permissões'
                }
                allowClear
                style={{ width: '100%' }}
                options={grupos}
                loading={grupos.length === 0}
                disabled={tipoUsuario === 'administrador'}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Nome de usuário e senha */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="nome_usuario"
              label="Nome de Usuário"
              rules={[{ required: true, message: 'Por favor, insira o nome de usuário' }]}
            >
              <Input disabled={userType !== 'administrador'} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="senha"
              label="Senha (deixe em branco para manter a atual)"
            >
              <Input.Password disabled={userType !== 'administrador'} />
            </Form.Item>
          </Col>
        </Row>

        {/* Horários */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="horario_entrada"
              label="Horário de Entrada"
            >
              <TimePicker format="HH:mm:ss" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="horario_saida"
              label="Horário de Saída"
            >
              <TimePicker format="HH:mm:ss" />
            </Form.Item>
          </Col>
          <Col span={6}>
                      <Form.Item
                        name="situacao"
                        label="Situação"
                        valuePropName="checked"
                      >
                        <Switch
                          checkedChildren="Ativo" 
                          unCheckedChildren="Bloqueado"
                        />
                      </Form.Item>
                    </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ModalEdit;
